<script lang="ts">
import {RouteNames} from "@/utils/constants.ts";

export function getRouteXDashboard() {
  return {
    name: RouteNames.X_DASHBOARD
  };
}
</script>

<script setup lang="ts">
import CurrencySelector from "../../components/CurrencySelector.vue";

import {
  IconBtc,
  IconEth,
  IconUsdt,
  IconXrp
} from "@iconify-prerendered/vue-cryptocurrency-color";
import DashboardBalancesWidget from "../../widgets/dashboard/DashboardBalancesWidget.vue";
import {getRouteXAccountsCardOrder} from "./accounts/card/OrderCard.vue";
import {getRouteXAccountsFiatSend} from "./accounts/fiat/Send.vue";
import {getRouteXAccountsCryptoSend} from "./accounts/crypto/SendCrypto.vue";
import {getRouteXAccountsCryptoBuy} from "./accounts/crypto/BuyCrypto.vue";
import {getRouteXAccountsCrypto} from "./accounts/Crypto.vue";
import PersonVerifiedBanner from "../../components/PersonVerifiedBanner.vue";
import {usePersonStore} from "@/store";
import {storeToRefs} from "pinia";
import {computed} from "vue";
import {useRouter} from "vue-router";

const router = useRouter();
const personStore = usePersonStore();
const { person } = storeToRefs(personStore);

// Check if user is authenticated
const isAuthenticated = computed(() => person.value !== null);

// Check if user has completed verification (IN_REVIEW or VERIFIED status)
const hasCompletedVerification = computed(() => {
  if (!person.value?.profile) {
    return false;
  }
  const status = person.value.profile.status;
  return status === "VERIFIED" || status === "IN_REVIEW";
});

// Show banner for authenticated users who haven't completed verification
const showOnboardingBanner = computed(() => isAuthenticated.value && !hasCompletedVerification.value);

// For guest users, show dashboard as demo (all interactions disabled)
// Authenticated users always have full access to dashboard
const isGuestMode = computed(() => !isAuthenticated.value);



type ICryptoAccount = {
  icon: any;
  title: string;
  price: number;
  change: number;
};

type ICurrency = {
  char: string;
  name: string;
  exponent: number;
};
type ICashAccount = {
  ccy: ICurrency;
  amount: number;
  color: string;
};
const accountsCrypto: ICryptoAccount[] = [
  {
    icon: IconBtc,
    title: "Bitcoin (BTC)",
    price: 52726.19,
    change: -3.94
  },
  {
    icon: IconEth,
    title: "Ethereum (ETH)",
    price: 52726.19,
    change: 3.94
  },
  {
    icon: IconUsdt,
    title: "TetherUS (USDT)",
    price: 52726.19,
    change: 3.94
  },
  {
    icon: IconXrp,
    title: "Ripple (XRP)",
    price: 52726.19,
    change: -3.94
  }
];

const accountsCash: ICashAccount[] = [
  {
    ccy: {
      char: "€",
      name: "EUR",
      exponent: 2
    },
    color: "#003399",
    amount: 0
  },
  {
    ccy: {
      char: "$",
      name: "USD",
      exponent: 2
    },
    color: "#6CDE07",
    amount: 0
  },
  {
    ccy: {
      char: "£",
      name: "GBP",
      exponent: 2
    },
    color: "#BC3FE0",
    amount: 0
  }
];

</script>

<template>
  <div class="relative">
    <!-- Animated background elements -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden opacity-60">
      <!-- Animated circles -->
      <div class="circle-glow circle-glow-1"></div>
      <div class="circle-glow circle-glow-2"></div>
      <div class="circle-glow circle-glow-3"></div>
      <div class="circle-glow circle-glow-4"></div>
      <div class="circle-glow circle-glow-5"></div>
      <div class="circle-glow circle-glow-6"></div>

      <!-- Floating wave shapes -->
      <svg class="wave-shape wave-1" viewBox="0 0 1200 300" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,100 Q300,50 600,100 T1200,100" stroke="rgba(99, 102, 241, 0.15)" stroke-width="2" fill="none"/>
      </svg>
      <svg class="wave-shape wave-2" viewBox="0 0 1200 300" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,150 Q300,100 600,150 T1200,150" stroke="rgba(147, 51, 234, 0.15)" stroke-width="2" fill="none"/>
      </svg>
      <svg class="wave-shape wave-3" viewBox="0 0 1200 300" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,200 Q300,150 600,200 T1200,200" stroke="rgba(59, 130, 246, 0.15)" stroke-width="2" fill="none"/>
      </svg>

      <!-- Geometric floating shapes - squares and circles -->
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
      <div class="floating-shape shape-4"></div>
      <div class="floating-shape shape-5"></div>
      <div class="floating-shape shape-6"></div>
      <div class="floating-shape shape-7"></div>
      <div class="floating-shape shape-8"></div>
    </div>

    <!-- Banner for guest users to encourage login -->
    <div v-if="isGuestMode" class="relative z-10 mb-5 min-h-[86px] w-full rounded-2xl shadow p-4 bg-gradient-to-r from-blue-500 to-purple-600">
      <div class="w-full text-white text-lg md:text-2xl font-bold font-['Sora'] leading-[38px] flex flex-col md:flex-row md:justify-between md:items-center items-center gap-4">
        <div class="flex items-center gap-3 text-center md:text-left w-full md:w-auto">
          <span class="text-4xl">👋</span>
          <span>Welcome! Login to access all features</span>
        </div>
      </div>
    </div>

    <!-- Banner for authenticated users who haven't completed onboarding -->
    <PersonVerifiedBanner v-else-if="showOnboardingBanner" class="relative z-10 mb-5" />

    <!-- Dashboard content - shown to all users, but disabled only for guests -->
    <div class="relative z-10" :class="{ 'pointer-events-none opacity-60 select-none': isGuestMode }">
      <!-- Dashboard Header Card -->
      <div class="backdrop-blur-xl rounded-2xl p-6 border bg-white/5 border-white/10 mb-6 transition-all duration-300 hover:bg-white/[0.07]">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <h1 class="text-xl md:text-2xl font-medium text-white">Dashboard</h1>

          <div class="flex items-center gap-3 flex-wrap">
            <button
              @click="router.push(getRouteXAccountsCryptoBuy())"
              class="flex items-center gap-3 px-6 py-3.5 text-base border bg-gray-800/30 border-gray-700/50 text-white hover:bg-gray-700/40 rounded-2xl transition-all duration-200"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              <span class="font-medium">Buy / Sell Crypto</span>
            </button>

            <button
              @click="router.push(getRouteXAccountsCrypto())"
              class="flex items-center gap-3 px-6 py-3.5 text-base border bg-gray-800/30 border-gray-700/50 text-white hover:bg-gray-700/40 rounded-2xl transition-all duration-200"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/>
              </svg>
              <span class="font-medium">Receive Crypto</span>
            </button>

            <CurrencySelector />
          </div>
        </div>
      </div>

      <DashboardBalancesWidget/>

      <div class="mt-6 flex flex-col lg:flex-row gap-6">
        <div class="flex flex-col gap-6 w-full lg:w-2/3">
          <!-- Payments and Transfers Card -->
          <div class="backdrop-blur-xl rounded-3xl p-6 border bg-gray-900/40 border-gray-800/30">
            <h2 class="text-white text-2xl font-medium mb-6">Payments and Transfers</h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Send Cash Button -->
              <button
                @click="router.push(getRouteXAccountsFiatSend())"
                class="px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 font-medium text-base"
              >
                <span>Send Cash</span>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
              </button>

              <!-- Send Crypto Button -->
              <button
                @click="router.push(getRouteXAccountsCryptoSend())"
                class="px-6 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 font-medium text-base"
              >
                <span>Send Crypto</span>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
              </button>

              <!-- Exchange Cash Button -->
              <button
                @click="router.push(getRouteXAccountsCryptoBuy())"
                class="px-6 py-4 bg-gray-800/60 hover:bg-gray-700/60 text-white border border-gray-700/50 rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 font-medium text-base"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                </svg>
                <span>Exchange Cash</span>
              </button>
            </div>
          </div>

          <!-- Transactions Section -->
          <div class="backdrop-blur-xl rounded-3xl p-6 border bg-gray-900/40 border-gray-800/30">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-white text-2xl font-medium">Transactions</h2>
              <button class="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                Show All
              </button>
            </div>

            <!-- Empty State -->
            <div class="flex flex-col items-center justify-center py-16 text-center">
              <svg class="w-16 h-16 text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 9l6 6m0-6l-6 6"/>
              </svg>
              <p class="text-gray-400 text-base">No transactions yet</p>
            </div>
          </div>

          <!-- Portfolio Section -->
          <div class="backdrop-blur-xl rounded-3xl p-6 border bg-gray-900/40 border-gray-800/30">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-white text-2xl font-medium">Portfolio</h2>
              <button class="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                Show All
              </button>
            </div>

            <!-- Empty State -->
            <div class="flex flex-col items-center justify-center py-16 text-center">
              <svg class="w-16 h-16 text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 9l6 6m0-6l-6 6"/>
              </svg>
              <p class="text-gray-400 text-base">No portfolio items</p>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-6 w-full lg:w-96">
          <!-- Cards Section -->
          <div class="backdrop-blur-xl rounded-3xl p-6 border bg-gray-900/40 border-gray-800/30">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-white text-2xl font-medium flex items-center gap-2">
                Cards
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7"/>
                </svg>
              </h2>
            </div>

            <!-- Add New Card Button -->
            <button
              @click="router.push(getRouteXAccountsCardOrder())"
              class="w-full px-6 py-4 border-2 border-dashed border-blue-600/50 bg-blue-600/5 hover:bg-blue-600/10 rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 text-blue-400 font-medium text-base"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
              </svg>
              Add New Card
            </button>
          </div>

          <!-- Cash Accounts Card -->
          <div class="backdrop-blur-xl rounded-2xl p-6 border bg-white/5 border-white/10 transition-all duration-300 hover:bg-white/[0.07]">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-2">
                <h3 class="text-white font-medium">Cash Accounts</h3>
                <svg class="w-5 h-5 text-gray-400 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </div>
              <button class="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                Show All
              </button>
            </div>
            <div class="space-y-4">
              <div v-for="account of accountsCash" :key="account.ccy.name"
                   class="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all cursor-pointer">
                <div
                    class="w-10 h-10 rounded-full flex justify-center text-white items-center select-none font-bold text-base"
                    :style="{ backgroundColor: account.color }"
                >
                  {{ account.ccy.char }}
                </div>
                <div class="flex-1">
                  <p class="text-white font-normal">{{ account.ccy.name }} Account</p>
                  <p class="text-gray-400 text-sm">{{ account.ccy.char }} {{ account.amount.toFixed(account.ccy.exponent) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Crypto Wallets Card -->
          <div class="backdrop-blur-xl rounded-2xl p-6 border bg-white/5 border-white/10 transition-all duration-300 hover:bg-white/[0.07]">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-2">
                <h3 class="text-white font-medium">Crypto Wallets</h3>
                <svg class="w-5 h-5 text-gray-400 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </div>
              <button class="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                Show All
              </button>
            </div>
            <div class="space-y-4">
              <div v-for="account of accountsCrypto" :key="account.title"
                   class="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all cursor-pointer">
                <component :is="account.icon" class="w-10 h-10 flex-shrink-0"/>
                <div class="flex-1">
                  <p class="text-white font-normal">{{ account.title }}</p>
                  <p class="text-gray-400 text-sm">${{ account.price.toFixed(2) }}</p>
                </div>
                <div class="flex items-center gap-1" :class="account.change > 0 ? 'text-green-500' : 'text-red-500'">
                  <svg v-if="account.change > 0" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/>
                  </svg>
                  <span class="text-sm">{{ account.change > 0 ? '+' : '' }}{{ account.change }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Animated Circle Glows */
.circle-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  animation: float 20s ease-in-out infinite;
}

.circle-glow-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.4), transparent);
  top: -10%;
  left: -10%;
  animation-delay: 0s;
}

.circle-glow-2 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(147, 51, 234, 0.4), transparent);
  bottom: -15%;
  right: -10%;
  animation-delay: -7s;
}

.circle-glow-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.4), transparent);
  top: 40%;
  right: 20%;
  animation-delay: -14s;
}

.circle-glow-4 {
  width: 380px;
  height: 380px;
  background: radial-gradient(circle, rgba(168, 85, 247, 0.4), transparent);
  bottom: 30%;
  left: 15%;
  animation-delay: -10s;
}

.circle-glow-5 {
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(79, 70, 229, 0.4), transparent);
  top: 55%;
  left: 5%;
  animation-delay: -5s;
}

.circle-glow-6 {
  width: 360px;
  height: 360px;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.4), transparent);
  bottom: 10%;
  right: 25%;
  animation-delay: -18s;
}

/* Wave Shapes */
.wave-shape {
  position: absolute;
  width: 100%;
  height: 200px;
  opacity: 1;
  animation: waveFlow 25s ease-in-out infinite;
}

.wave-1 {
  top: 20%;
  left: -10%;
  animation-delay: 0s;
}

.wave-2 {
  top: 45%;
  right: -10%;
  animation-delay: -8s;
}

.wave-3 {
  bottom: 15%;
  left: 5%;
  animation-delay: -16s;
}

/* Floating Geometric Shapes */
.floating-shape {
  position: absolute;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1));
  border: 1px solid rgba(99, 102, 241, 0.15);
  animation: floatShape 18s ease-in-out infinite;
}

.shape-1 {
  width: 120px;
  height: 120px;
  top: 10%;
  left: 15%;
  animation-delay: 0s;
  transform: rotate(25deg);
}

.shape-2 {
  width: 80px;
  height: 80px;
  top: 60%;
  right: 10%;
  animation-delay: -6s;
  transform: rotate(-15deg);
  border-radius: 50%;
}

.shape-3 {
  width: 100px;
  height: 100px;
  bottom: 25%;
  left: 45%;
  animation-delay: -12s;
  transform: rotate(45deg);
}

.shape-4 {
  width: 60px;
  height: 60px;
  top: 35%;
  left: 70%;
  animation-delay: -9s;
  transform: rotate(-30deg);
  border-radius: 12px;
}

.shape-5 {
  width: 90px;
  height: 90px;
  top: 25%;
  right: 35%;
  animation-delay: -3s;
  transform: rotate(60deg);
  border-radius: 50%;
}

.shape-6 {
  width: 70px;
  height: 70px;
  bottom: 40%;
  left: 25%;
  animation-delay: -15s;
  transform: rotate(-45deg);
}

.shape-7 {
  width: 110px;
  height: 110px;
  top: 70%;
  right: 20%;
  animation-delay: -7s;
  transform: rotate(30deg);
}

.shape-8 {
  width: 50px;
  height: 50px;
  bottom: 50%;
  right: 5%;
  animation-delay: -11s;
  transform: rotate(-60deg);
  border-radius: 50%;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(30px, -30px) scale(1.1);
  }
  50% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  75% {
    transform: translate(40px, 10px) scale(1.05);
  }
}

/* Floating Boxes */
.floating-box {
  position: absolute;
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  animation: floatSlow 15s ease-in-out infinite;
}

.floating-box-1 {
  width: 200px;
  height: 200px;
  top: 15%;
  left: 10%;
  animation-delay: -3s;
  transform: rotate(45deg);
}

.floating-box-2 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  left: 60%;
  animation-delay: -8s;
  transform: rotate(-30deg);
}

@keyframes floatSlow {
  0%, 100% {
    transform: translate(0, 0) rotate(45deg);
    opacity: 0.1;
  }
  50% {
    transform: translate(20px, -20px) rotate(60deg);
    opacity: 0.2;
  }
}

/* Wave Animation */
@keyframes waveFlow {
  0%, 100% {
    transform: translateX(0) translateY(0);
    opacity: 1;
  }
  25% {
    transform: translateX(30px) translateY(-15px);
    opacity: 0.8;
  }
  50% {
    transform: translateX(-20px) translateY(10px);
    opacity: 1;
  }
  75% {
    transform: translateX(15px) translateY(-10px);
    opacity: 0.9;
  }
}

/* Floating Shape Animation */
@keyframes floatShape {
  0%, 100% {
    transform: translate(0, 0) rotate(25deg);
    opacity: 0.15;
  }
  33% {
    transform: translate(20px, -30px) rotate(35deg);
    opacity: 0.25;
  }
  66% {
    transform: translate(-15px, 25px) rotate(15deg);
    opacity: 0.2;
  }
}

/* Responsive animations */
@media (max-width: 768px) {
  .circle-glow-1,
  .circle-glow-2,
  .circle-glow-3,
  .circle-glow-4,
  .circle-glow-5,
  .circle-glow-6 {
    width: 250px;
    height: 250px;
  }

  .floating-box-1,
  .floating-box-2 {
    width: 100px;
    height: 100px;
  }

  .wave-shape {
    height: 150px;
  }

  .floating-shape {
    opacity: 0.5;
  }

  .shape-1,
  .shape-3,
  .shape-5,
  .shape-7 {
    width: 80px;
    height: 80px;
  }

  .shape-2,
  .shape-4,
  .shape-6,
  .shape-8 {
    width: 50px;
    height: 50px;
  }
}
</style>
